import React from 'react';
import { Handle, Position } from 'reactflow';
import { GitBranch, CheckCircle, XCircle, Clock, Trash2, AlertCircle, Sparkles } from 'lucide-react';
import { NodeData, Condition } from '../../../types';
import { useFlowStore } from '../../../store';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import Tooltip from '../../common/Tooltip';
import { useDeleteStep } from '../../../hooks/queries/useStepMutations';

interface ConditionalNodeProps {
  data: NodeData;
  selected: boolean;
}

const ConditionalNode: React.FC<ConditionalNodeProps> = ({ data, selected }) => {
  const { setSelectedNode, isModelModalOpen, setIsModelModalOpen, nodes, edges, setNodes, setEdges } = useFlowStore();
  const [isDeletable, setIsDeletable] = React.useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  // AC-1406: Delete via React Query mutation hook (see AgentNode.tsx for
  // the rationale on why local canvas state is updated alongside the
  // hook-driven RQ invalidation).
  const deleteStepMutation = useDeleteStep(data.step?.orchestration_id ?? '');

  // Debug logging to identify the issue
  React.useEffect(() => {
    console.log('[ConditionalNode] Data received:', data);
    console.log('[ConditionalNode] Step data:', data.step);
    console.log('[ConditionalNode] on_true:', data.step?.on_true);
    console.log('[ConditionalNode] on_false:', data.step?.on_false);
  }, [data]);

  // Check if node can be deleted (not referenced by other nodes)
  React.useEffect(() => {
    if (!data.step || !data.step.orchestration_step_id) {
      setIsDeletable(false);
      return;
    }
    const canDelete = !nodes.some(
      n =>
        n.id !== data.id &&
        n.data?.step?.previous_step_id &&
        data.step?.orchestration_step_id &&
        String(n.data.step.previous_step_id) === String(data.step.orchestration_step_id)
    );
    setIsDeletable(canDelete);
  }, [nodes, data.id, data.step?.orchestration_step_id, data.step]);

  const handleDelete = async () => {
    if (!data.step?.orchestration_step_id) return;
    try {
      await deleteStepMutation.mutateAsync({
        stepId: data.step.orchestration_step_id,
      });
      setNodes(nodes.filter(node => node.id !== data.id));
      setEdges(edges.filter(edge => edge.source !== data.id && edge.target !== data.id));
    } catch (error) {
      console.error('Failed to delete step:', error);
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <Clock className="w-4 h-4 text-primary-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/80';
      case 'completed':
        return 'border-green-500 bg-green-50 dark:bg-green-900/80';
      case 'failed':
        return 'border-red-500 bg-red-50 dark:bg-red-900/80';
      default:
        return 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-gray-800';
    }
  };

  // Parse conditions - handle both string and object formats with robust nested field support
  const parseConditions = (): Condition[] => {
    const rawConditions = data.step?.condition?.conditions || [];
    if (!rawConditions.length) return [];
    
    // Handle mixed array of strings and objects
    return rawConditions.map((cond: string | Condition) => {
      if (typeof cond === 'object' && 'field' in cond) {
        return cond;
      }
      
      if (typeof cond === 'string') {
        // Enhanced parsing to handle nested field paths
        const trimmedCond = cond.trim();
        if (!trimmedCond) return { field: '', operator: '', value: '' };
        
        // Define supported operators in order of precedence (longer operators first)
        const operators = ['==', '!=', '>=', '<=', '>', '<'];
        
        // Find the operator in the condition string
        let foundOperator = '';
        let operatorIndex = -1;
        
        for (const op of operators) {
          const index = trimmedCond.indexOf(op);
          if (index !== -1) {
            // Make sure this isn't part of a nested field path or value
            // Check that there are spaces or word boundaries around the operator
            const beforeChar = index > 0 ? trimmedCond[index - 1] : ' ';
            const afterChar = index + op.length < trimmedCond.length ? trimmedCond[index + op.length] : ' ';
            
            if ((beforeChar === ' ' || /\w/.test(beforeChar)) &&
                (afterChar === ' ' || /\w/.test(afterChar) || afterChar === '"' || afterChar === "'")) {
              foundOperator = op;
              operatorIndex = index;
              break;
            }
          }
        }
        
        if (!foundOperator || operatorIndex === -1) {
          // Fallback: try to split by whitespace for simple cases
          const parts = trimmedCond.split(/\s+/);
          if (parts.length >= 3) {
            const operator = parts[1];
            const field = parts[0];
            const value = parts.slice(2).join(' ').replace(/^["'](.*)["']$/, '$1');
            return { field, operator, value };
          }
          return { field: '', operator: '', value: '' };
        }
        
        // Extract field (everything before the operator, trimmed)
        const field = trimmedCond.slice(0, operatorIndex).trim();
        
        // Extract value (everything after the operator, trimmed and unquoted)
        const valueStart = operatorIndex + foundOperator.length;
        let value = trimmedCond.slice(valueStart).trim();
        
        // Remove surrounding quotes if present
        if ((value.startsWith('"') && value.endsWith('"')) ||
            (value.startsWith("'") && value.endsWith("'"))) {
          value = value.slice(1, -1);
        }
        
        return {
          field,
          operator: foundOperator,
          value
        };
      }
      
      return { field: '', operator: '', value: '' };
    });
  };

  const conditions = parseConditions();

  return (
    <div className="relative">
      <div
        className={`relative w-72 h-32 px-4 py-3 rounded-lg border-2 shadow-lg transition-all duration-200
          ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
          bg-gradient-to-br ${getStatusColor()}
          hover:shadow-xl hover:border-opacity-100`}
      >
        {/* Delete button - only show if node is deletable */}
        {isDeletable && (
          <button
              title="Delete step"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
        )}

        {/* Conditional Badge - Similar to UserPromptNode */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-sm font-semibold tracking-wide shadow-md whitespace-nowrap z-20">
          <div className="flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            <span>Conditional</span>
            {data.step?.feedback_loop_hints && Object.keys(data.step.feedback_loop_hints).length > 0 && (
              <Tooltip content="Feedback Loop Hints configured">
                <div>
                  <Sparkles className="w-3 h-3" />
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {/*
          Passthrough badge (AC-1247): every conditional step now carries upstream
          data through its envelope so downstream steps (queue_write, agents) see
          the real producer's payload. The badge makes that behavior visible at
          design time. Always rendered; no logic-driven visibility.
        */}
        <div
          className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 px-2 py-0.5 rounded-full bg-gray-700/90 dark:bg-gray-200/90 text-white dark:text-gray-900 text-[10px] font-medium tracking-wide shadow whitespace-nowrap z-20"
          data-testid="conditional-passthrough-badge"
        >
          passes upstream data through
        </div>

        {/* Main Card Content */}
        <div className="flex flex-col items-center py-2 w-full">
          {getStatusIcon() && (
            <div className="flex items-center gap-2 mb-2 w-full justify-center">
              {getStatusIcon()}
            </div>
          )}
          
          {/* Conditions Display */}
          {conditions.length > 0 && (
            <div className="px-3 py-2 rounded bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs border border-yellow-200 dark:border-yellow-700 mx-2 self-stretch mb-2">
              <div className="font-medium mb-1">Conditions:</div>
              {conditions.slice(0, 1).map((condition: Condition, index: number) => {
                if (!condition.field) return null;
                
                const formatFieldPath = (field: string) => {
                  if (field.includes('.')) {
                    const parts = field.split('.');
                    if (parts.length > 2) {
                      return `${parts[0]}...${parts[parts.length - 1]}`;
                    }
                    return field;
                  }
                  return field;
                };
                
                const displayField = formatFieldPath(condition.field);
                const displayValue = String(condition.value);
                
                return (
                  <Tooltip key={index} content={`${condition.field} ${condition.operator} ${condition.value}`}>
                    <div className="text-xs leading-tight break-words">
                      <span className="font-mono text-blue-700 dark:text-blue-300">{displayField}</span>
                      <span className="text-gray-600 dark:text-gray-400 mx-1">{condition.operator}</span>
                      <span className="font-mono text-green-700 dark:text-green-300">{displayValue}</span>
                    </div>
                  </Tooltip>
                );
              })}
              {conditions.length > 1 && (
                <div className="text-yellow-600 dark:text-yellow-400 mt-1 text-xs">
                  +{conditions.length - 1} more...
                </div>
              )}
            </div>
          )}
          
          {/* Actions Display with T/F badges */}
          <div className="flex items-center justify-center gap-5 text-xs px-4 py-1">
            <div className="flex items-center space-x-1.5">
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center text-white text-[9px] font-bold leading-none flex-shrink-0">
                T
              </div>
              <span className="text-gray-600 dark:text-gray-400 font-medium">
                {data.step?.on_true && typeof data.step.on_true === 'object' && 'action' in data.step.on_true
                  ? String(data.step.on_true.action)
                  : 'continue'}
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-[9px] font-bold leading-none flex-shrink-0">
                F
              </div>
              <span className="text-gray-600 dark:text-gray-400 font-medium">
                {data.step?.on_false && typeof data.step.on_false === 'object' && 'action' in data.step.on_false
                  ? String(data.step.on_false.action)
                  : 'feedback_loop'}
              </span>
            </div>
          </div>
        </div>
        
        {data.status === 'running' && (
          <div className="absolute inset-0 rounded-lg border-2 border-blue-500 animate-pulse" />
        )}
        
        {/* Target handles for dynamic edge connections */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        
        {/* Source handles for dynamic outgoing connections */}
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        
        {/* Dedicated feedback loop handles - positioned to avoid conflicts */}
        <Handle
          type="source"
          position={Position.Top}
          id="feedback-out-top"
          style={{ top: 0, left: '30%', transform: 'translate(-50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="feedback-out-left"
          style={{ left: 0, top: '30%', transform: 'translate(-50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Top}
          id="feedback-in-top"
          style={{ top: 0, left: '70%', transform: 'translate(-50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Right}
          id="feedback-in-right"
          style={{ right: 0, top: '30%', transform: 'translate(50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
      </div>
      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Step"
          message="Are you sure you want to delete this step? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            handleDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default ConditionalNode;